/**
 * @description UserProfile — account and security settings for merchant / SO users.
 *
 * Key features:
 * - Account tab: view/edit first & last name; email and company role are read-only
 * - Security tab: change password; sign out / sign out everywhere
 * - Light Untitled UI treatment matching Company Settings / invoices
 *
 * @dependencies
 * - AuthContext: useUser, useAuth, useCompany
 * - apiClient: PATCH /auth/profile, POST /auth/change-password, POST /auth/logout-all
 * - @headlessui/react: Tab, Dialog, Transition
 * - resolveProfileRoleDisplay: company RBAC label, never platform "Merchant"
 *
 * @notes
 * - Profile updates stay local-DB name-only (PATCH /api/auth/profile)
 * - Password change uses Wristband via POST /api/auth/change-password
 * - Sign out everywhere calls POST /api/auth/logout-all then Wristband logout
 * - Email is not editable; Wristband login identifier is not wired here
 */

import React, { Fragment, useState } from 'react';
import { Dialog, Tab, Transition } from '@headlessui/react';
import { User, Mail, Shield, Building, Key, Edit2, X, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useUser, useAuth, useCompany } from '../contexts/AuthContext';
import apiClient from '../services/api/apiClient';
import { resolveProfileRoleDisplay } from '../utils/profileRoleDisplay';

const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: '8+ characters', test: (pw) => pw.length >= 8 },
  { id: 'uppercase', label: 'Uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lowercase', label: 'Lowercase', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'Number', test: (pw) => /[0-9]/.test(pw) },
];

const INPUT_CLASS =
  'w-full h-11 px-4 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-shadow';
const READONLY_CLASS =
  'w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 flex items-center gap-2';

const UserProfile = () => {
  const { user, isLoaded } = useUser();
  const { signOut, refreshContext, companies, activeCompanyId } = useAuth();
  const { company } = useCompany();
  const [activeTab, setActiveTab] = useState(0);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [logoutAllError, setLogoutAllError] = useState(null);

  if (!isLoaded) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <p className="text-gray-500">Please sign in to view your profile.</p>
      </div>
    );
  }

  const displayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.email || 'User';

  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  const roleDisplayName = resolveProfileRoleDisplay({
    companies,
    activeCompanyId: activeCompanyId || company?.id,
    platformRole: user?.role
  });

  const handleStartEdit = () => {
    setEditForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || ''
    });
    setEditError(null);
    setEditSuccess(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const handleSaveProfile = async () => {
    setEditLoading(true);
    setEditError(null);
    setEditSuccess(false);

    try {
      const response = await apiClient.patch('/auth/profile', {
        firstName: editForm.firstName,
        lastName: editForm.lastName
      });

      if (response.data.success) {
        setEditSuccess(true);
        setIsEditing(false);
        if (refreshContext) {
          await refreshContext();
        }
        setTimeout(() => setEditSuccess(false), 3000);
      } else {
        setEditError('Failed to update profile');
      }
    } catch (error) {
      setEditError(error.response?.data?.error?.message || error.message || 'Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenPasswordModal = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setPasswordSuccess(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
  };

  const isPasswordValid = (password) => {
    return PASSWORD_REQUIREMENTS.every(req => req.test(password));
  };

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }
    if (!passwordForm.newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }
    if (!isPasswordValid(passwordForm.newPassword)) {
      setPasswordError('Password does not meet all requirements');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    setPasswordError(null);

    try {
      const response = await apiClient.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      if (response.data.success) {
        setPasswordSuccess(true);
        setTimeout(() => {
          handleClosePasswordModal();
          setPasswordSuccess(false);
        }, 2000);
      } else {
        setPasswordError('Failed to change password');
      }
    } catch (error) {
      const errorCode = error.response?.data?.error?.code;
      const errorMessage = error.response?.data?.error?.message;

      if (errorCode === 'AUTH_INVALID_CREDENTIALS' || errorMessage?.includes('invalid')) {
        setPasswordError('Current password is incorrect');
      } else if (errorMessage) {
        setPasswordError(errorMessage);
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSignOutEverywhere = async () => {
    setLogoutAllLoading(true);
    setLogoutAllError(null);

    try {
      const response = await apiClient.post('/auth/logout-all');
      if (response.data?.success !== true || !response.data?.logoutUrl) {
        throw new Error(
          response.data?.error?.message || 'Failed to sign out everywhere'
        );
      }

      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('token_expires_at');
      sessionStorage.removeItem('wristband_auth_request_token');
      delete apiClient.defaults.headers.common['Authorization'];

      window.location.href = response.data.logoutUrl;
    } catch (error) {
      setLogoutAllError(
        error.response?.data?.error?.message || error.message || 'Failed to sign out everywhere'
      );
      setLogoutAllLoading(false);
    }
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your personal details and security.</p>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-full bg-brand flex items-center justify-center text-white text-lg font-semibold">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">{displayName}</p>
          <p className="text-sm text-gray-500 truncate">{user?.email}</p>
          {roleDisplayName && (
            <span className="inline-flex items-center gap-1 mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand/10 text-brand border border-brand/20">
              <Building className="w-3 h-3" />
              {roleDisplayName}
            </span>
          )}
        </div>
      </div>

      {editSuccess && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700 text-sm">
          <Check className="w-4 h-4" />
          Profile updated successfully
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
          <Tab.List className="flex border-b border-gray-200 px-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab as={Fragment} key={tab.id}>
                  {({ selected }) => (
                    <button
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 outline-none transition-colors ${
                        selected
                          ? 'border-brand text-brand'
                          : 'border-transparent text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  )}
                </Tab>
              );
            })}
          </Tab.List>

          <Tab.Panels>
            <Tab.Panel className="outline-none">
              <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Personal info</h2>
                  <p className="text-sm text-gray-500 mt-1">Update your personal details here.</p>
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>

              <div className="p-6 space-y-5">
                {editError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {editError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      First name <span className="text-brand">*</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.firstName}
                        onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="First name"
                      />
                    ) : (
                      <div className={READONLY_CLASS}>
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{user?.firstName || '\u2014'}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Last name <span className="text-brand">*</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.lastName}
                        onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="Last name"
                      />
                    ) : (
                      <div className={READONLY_CLASS}>
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{user?.lastName || '\u2014'}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address
                  </label>
                  <div className={READONLY_CLASS} data-testid="profile-email">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="truncate">{user?.email || '\u2014'}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                    <div className={READONLY_CLASS}>
                      <Shield className="w-4 h-4 text-gray-400" />
                      <span>{roleDisplayName || '\u2014'}</span>
                    </div>
                  </div>
                  {company?.businessName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Company</label>
                      <div className={READONLY_CLASS}>
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="truncate">{company.businessName}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={editLoading}
                    className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveProfile}
                    disabled={editLoading}
                    className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-brand hover:bg-brand-hover rounded-lg transition-colors disabled:opacity-50"
                  >
                    {editLoading ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Save changes
                  </button>
                </div>
              )}
            </Tab.Panel>

            <Tab.Panel className="outline-none">
              <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">Security</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your password and sessions.</p>
              </div>

              <div className="p-6 space-y-6">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg border border-gray-200">
                      <Key className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">Password</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Change your password to keep your account secure.
                      </p>
                      <button
                        type="button"
                        onClick={handleOpenPasswordModal}
                        className="mt-3 px-4 py-2 text-sm font-semibold text-brand bg-brand/10 hover:bg-brand/20 border border-brand/20 rounded-lg transition-colors"
                      >
                        Change Password
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100 space-y-3">
                  {logoutAllError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {logoutAllError}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={signOut}
                      className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Sign Out
                    </button>
                    <button
                      type="button"
                      onClick={handleSignOutEverywhere}
                      disabled={logoutAllLoading}
                      className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {logoutAllLoading ? 'Signing out\u2026' : 'Sign out everywhere'}
                    </button>
                  </div>
                </div>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      <Transition.Root show={showPasswordModal} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => { if (!passwordLoading) handleClosePasswordModal(); }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl border border-gray-200">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Change Password
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={handleClosePasswordModal}
                      className="text-gray-400 hover:text-gray-700 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {passwordSuccess ? (
                    <div className="text-center py-10 px-6">
                      <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-6 h-6 text-emerald-600" />
                      </div>
                      <h4 className="text-lg font-medium text-gray-900">Password Changed</h4>
                      <p className="text-sm text-gray-500 mt-1">Your password has been updated successfully.</p>
                    </div>
                  ) : (
                    <div className="p-6 space-y-4">
                      {passwordError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          {passwordError}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Current password</label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                            className={INPUT_CLASS}
                            placeholder="Enter current password"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                            aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                          >
                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">New password</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            className={INPUT_CLASS}
                            placeholder="Enter new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                            aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                          >
                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>

                        {passwordForm.newPassword && (
                          <div className="mt-2 grid grid-cols-2 gap-1.5 p-3 rounded-xl bg-gray-50 border border-gray-200">
                            {PASSWORD_REQUIREMENTS.map(req => {
                              const passed = req.test(passwordForm.newPassword);
                              return (
                                <div
                                  key={req.id}
                                  className={`flex items-center gap-1.5 text-[11px] ${
                                    passed ? 'text-emerald-600' : 'text-gray-400'
                                  }`}
                                >
                                  <div className={`w-1 h-1 rounded-full ${passed ? 'bg-emerald-600' : 'bg-gray-400'}`} />
                                  {req.label}
                                </div>
                              );
                            })}
                            <div className={`flex items-center gap-1.5 text-[11px] ${
                              passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.confirmPassword
                                ? 'text-emerald-600' : 'text-gray-400'
                            }`}>
                              <div className={`w-1 h-1 rounded-full ${
                                passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.confirmPassword
                                  ? 'bg-emerald-600' : 'bg-gray-400'
                              }`} />
                              Passwords match
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm new password</label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                          className={INPUT_CLASS}
                          placeholder="Confirm new password"
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={handleClosePasswordModal}
                          className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleChangePassword}
                          disabled={passwordLoading || !isPasswordValid(passwordForm.newPassword) || passwordForm.newPassword !== passwordForm.confirmPassword || !passwordForm.currentPassword}
                          className="px-4 py-2.5 text-sm font-semibold text-white bg-brand hover:bg-brand-hover rounded-lg disabled:opacity-50 flex items-center gap-2"
                        >
                          {passwordLoading && (
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          )}
                          Change Password
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default UserProfile;
